import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { withStyles, WithStyles, Theme, createStyles } from "@material-ui/core";
import { withRouter } from "react-router-dom";

import { checkLoginStatus } from "../api/index";
import { updateCapabilities } from "../actions/store/app-actions";
import { StoreState } from "../reducers/root-reducer";
import { InjectedRouterProps } from "../router/props";
import { ConnectedLogin } from "./login";
import { ConnectedSnackbar } from "./snackbar";
import { ConnectedMainApp } from "./main-app";
import { SnackbarVariant } from "../actions/store/type";
import { setSnackbarMessage } from "../actions/store/snackbar-actions";
import { startDragging, endDragging } from "../actions/store/ui-actions";
import { Capabilities, getCapabilities } from "../integration/capabilities";
import { IntegrationCommunicator } from "../integration";
import { Inquiry } from "../api/inquiries";
import {
	changeInquiryListDateRange,
	changeHighlightedInquiry,
	toggleArchiveVisibility
} from "../actions/store/inquiry-list-actions";
import { setLoginStatusAndAuthInfo } from "../actions/auth-actions";
import { sendClientData } from "../api/auth";

export interface RootProps {}

interface State {
	ready: boolean;
}

interface ConnectedRootProps extends WithStyles<typeof styles> {
	isAppReady: boolean;
	isLoggedIn: boolean;
	inquiries: Inquiry[];
	capabilities: Capabilities;
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) => void;
	startDragging: () => void;
	endDragging: () => void;
	updateCapabilities: (capabilities: Capabilities) => void;
	changeInquiryListDateRange: (startDate: Date, endDate: Date) => void;
	changeHighlightedInquiry: (id: string) => void;
	toggleArchiveVisibility: (state: boolean) => void;
}

type Props = RootProps & ConnectedRootProps & InjectedRouterProps;

export class Root extends React.Component<Props, {}> {
	public state: State = {
		ready: false
	};

	public componentDidMount() {
		const { startDragging, endDragging, updateCapabilities } = this.props;

		document.addEventListener("dragstart", () => startDragging());
		document.addEventListener("dragend", () => endDragging());

		IntegrationCommunicator.start();
		this.handleDefaultIntegrationEvents();

		window.addEventListener("load", async () => {
			const isLoggedIn = await checkLoginStatus();
			setLoginStatusAndAuthInfo(isLoggedIn);

			const capabilities = await getCapabilities();
			updateCapabilities(capabilities);

			setTimeout(() => IntegrationCommunicator.send("ready", null), 300);
			this.setState({
				ready: true
			});
		});
	}

	private handleDefaultIntegrationEvents = () => {
		IntegrationCommunicator.on("notification-action", data => {
			const payload = (data.payload as any).data as any;

			if (payload.type === "inquiry") {
				const newInquiry = this.props.inquiries.find(
					inq => inq.id === payload.subject
				);

				if (newInquiry) {
					this.props.history.push("/inquiry-list");
					const startDate = new Date(newInquiry.date);
					const endDate = new Date(newInquiry.date);

					startDate.setHours(0, 0, 0, 0);
					endDate.setHours(23, 59, 59, 999);

					this.props.toggleArchiveVisibility(false);
					this.props.changeInquiryListDateRange(startDate, endDate);
					this.props.changeHighlightedInquiry(newInquiry.id);
				}
			}
		});
	};

	render() {
		const { classes } = this.props;
		const { ready } = this.state;

		if (!ready) {
			return null;
		}

		return (
			<React.Fragment>
				{this.props.isAppReady && (
					<div className={classes.mainContent}>
						{this.props.isLoggedIn ? (
							<ConnectedMainApp />
						) : (
							<ConnectedLogin />
						)}
					</div>
				)}
				<div className={classes.coverImage} />
				<ConnectedSnackbar />
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	isAppReady: state.isAppReady,
	isLoggedIn: state.isLoggedIn,
	inquiries: state.inquiries,
	capabilities: state.capabilities
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant)),
	startDragging: () => dispatch(startDragging()),
	endDragging: () => dispatch(endDragging()),
	updateCapabilities: (capabilities: Capabilities) =>
		dispatch(updateCapabilities(capabilities)),
	changeInquiryListDateRange: (startDate: Date, endDate: Date) =>
		dispatch(changeInquiryListDateRange(startDate, endDate)),
	changeHighlightedInquiry: (id: string) =>
		dispatch(changeHighlightedInquiry(id)),
	toggleArchiveVisibility: (state: boolean) => dispatch(toggleArchiveVisibility(state))
});

const styles = (theme: Theme) =>
	createStyles({
		mainContent: {
			position: "relative",
			display: "flex",
			flexDirection: "column",
			flexGrow: 1
		},
		coverImage: {
			background: "lightgray",
			backgroundRepeat: "no-repeat",
			backgroundPosition: "50% 50%",
			backgroundSize: "cover",
			position: "fixed",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			zIndex: -1,
			filter: "blur(12px)",
			transform: "scale(1.1)"
		}
	});

export const ConnectedRoot = withRouter(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(Root)
	)
);
